import { useState, useCallback } from 'react';
import { CircularProgress } from '../CircularProgress';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend,
} from 'recharts';
import { KPICell, SectionFrame, performanceColor } from '../scheduled/shared';
import { useQualityMeasureLabel } from '../../../lib/qualityLabels';
import { usePreferences } from '../../../contexts/PreferencesContext';
import type { POLineDrilldownMode } from '../../../lib/api/po-line-dashboard';
import { POLineDrilldownModal } from './POLineDrilldownModal';
import { dayIsoFor } from './poLineDrilldownModes';
import { deliveryTabCounts, type POLinePeriodProps, type POLineDrilldownState } from './types';
import { blockTarget } from '../periodTarget';
import { showsOtd, showsOtif } from '../deliveryMetricMode';

const PALETTE = {
  onTime: '#0f766e',
  early:  '#2563eb',
  late:   '#dc2626',
  pending: '#cbd5e1',
};

/**
 * Current Week panel of the PO Line Dashboard — clone of the Scheduled
 * Dashboard's CurrentWeek with "PO lines" wording and PO-line drill-downs.
 */
export function POLineCurrentWeek({ data, formatCurrency, filters, onRefresh }: POLinePeriodProps) {
  const cw = data.current_week;
  const tabCounts = deliveryTabCounts(cw.delivery);
  const qualityLabel = useQualityMeasureLabel();
  const { preferences } = usePreferences();
  const kpiDecimals = preferences.kpi_percent_decimals ?? 0;

  // CR-6266: the target at the end of the tile's own window. CR-7714 FE-20:
  // no target set means no colour, never an invented 95.
  const otifTarget = blockTarget(cw, 'otif', data.otif_target ?? null);
  const otdTarget = blockTarget(cw, 'otd', data.otd_target ?? null);
  const deliveryMode = data.delivery_metric_mode;  // CR-8697: the helpers know 'none'
  const showOTIF = showsOtif(deliveryMode);
  const showOTD  = showsOtd(deliveryMode);

  const isLive = cw.is_live ?? true;
  const periodWord = isLive ? 'this' : 'that';
  const weekStartIso = cw.window_start;
  const weekEndIso   = cw.window_end;
  const otifStartIso = cw.otif_window_start;
  const otifEndIso   = cw.otif_window_end;

  const [modal, setModal] = useState<POLineDrilldownState | null>(null);
  const openWeek = useCallback((mode: POLineDrilldownMode) => {
    setModal({ mode, periodStart: weekStartIso, periodEnd: weekEndIso, periodGrain: 'week', periodLabel: isLive ? 'This week' : 'That week', tabCounts });
  }, [weekStartIso, weekEndIso, isLive, tabCounts]);
  const openOtifWindow = useCallback((mode: POLineDrilldownMode) => {
    setModal({ mode, periodStart: otifStartIso, periodEnd: otifEndIso, periodGrain: 'week', periodLabel: isLive ? 'Week to date' : 'Full week', tabCounts, tabContext: 'otd_mix' });
  }, [otifStartIso, otifEndIso, isLive, tabCounts]);
  const openDay = useCallback((mode: POLineDrilldownMode, dayIso: string, bar?: { onTime: number; early: number; late: number; pending: number }) => {
    setModal({
      mode, periodStart: dayIso, periodEnd: dayIso, periodGrain: 'week',
      tabCounts: bar ? { grn_on_time: bar.onTime, grn_early: bar.early, grn_late: bar.late, open: bar.pending } : undefined,
      tabContext: 'bars',
    });
  }, []);

  const deliveriesByDay = cw.deliveries_by_day.map((d) => ({
    day: d.day, onTime: d.on_time, early: d.early, late: d.late, pending: d.pending,
  }));

  const otdSplit = [
    { name: 'On time', value: cw.delivery.on_time, color: PALETTE.onTime, mode: 'grn_on_time' as const },
    { name: 'Early',   value: cw.delivery.early,   color: PALETTE.early,  mode: 'grn_early' as const },
    { name: 'Late',    value: cw.delivery.late,    color: PALETTE.late,   mode: 'grn_late' as const },
  ];
  const otdEmpty = otdSplit.every((d) => d.value === 0);
  const otdData: Array<{ name: string; value: number; color: string; mode: POLineDrilldownMode | null }> = otdEmpty
    ? [{ name: 'No data yet', value: 1, color: '#f1f5f9', mode: null }]
    : otdSplit;

  const otifPass    = cw.delivery.passed  ?? 0;
  const otifPending = cw.delivery.pending ?? 0;
  const otifFail    = cw.delivery.failed  ?? 0;
  // CR-6327: arrears are their own outcome — see POLineCurrentMonth.
  const otifArrears = cw.delivery.arrears ?? 0;
  const otifSplit = [
    { name: 'Passed',  value: otifPass,    color: PALETTE.onTime, mode: 'otif_passed' as const },
    { name: 'Pending', value: otifPending, color: '#94a3b8',      mode: 'otif_pending' as const },
    { name: 'Arrears', value: otifArrears, color: '#f59e0b',      mode: 'otif_arrears' as const },
    { name: 'Failed',  value: otifFail,    color: '#dc2626',      mode: 'otif_failed' as const },
  ];
  const otifEmpty = otifSplit.every((d) => d.value === 0);
  const otifData: Array<{ name: string; value: number; color: string; mode: POLineDrilldownMode | null }> = otifEmpty
    ? [{ name: 'No data yet', value: 1, color: '#f1f5f9', mode: null }]
    : otifSplit;

  // Per-segment click handler (see CurrentWeek.tsx for why BarChart.onClick is
  // not enough — it always reports the first series).
  const onSegmentClick = (segment: 'onTime' | 'early' | 'late' | 'pending') => (raw: unknown) => {
    const dayRaw = raw && typeof raw === 'object'
      ? (raw as { payload?: { day?: unknown }; day?: unknown }).day
        ?? (raw as { payload?: { day?: unknown } }).payload?.day
      : undefined;
    if (typeof dayRaw !== 'string') return;
    const iso = dayIsoFor(weekStartIso, dayRaw);
    const bar = deliveriesByDay.find((d) => d.day === dayRaw);
    if (segment === 'onTime')       openDay('grn_on_time', iso, bar);
    else if (segment === 'early')   openDay('grn_early', iso, bar);
    else if (segment === 'late')    openDay('grn_late', iso, bar);
    else if (segment === 'pending') openDay('open', iso, bar);
  };

  const handleOtdSlice = (slice: unknown) => {
    const m = slice && typeof slice === 'object' ? (slice as { mode?: unknown }).mode : undefined;
    if (typeof m === 'string') openOtifWindow(m as POLineDrilldownMode);
  };
  const handleOtifSlice = (slice: unknown) => {
    const m = slice && typeof slice === 'object' ? (slice as { mode?: unknown }).mode : undefined;
    if (typeof m === 'string') openWeek(m as POLineDrilldownMode);
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-3 gap-4">
        <SectionFrame title="Quality" accent="red" subtitle={isLive ? 'Mon → today' : 'full week'}>
          <div className="grid grid-cols-2 gap-3">
            <KPICell
              label={qualityLabel}
              value={<span className="text-2xl font-semibold tabular-nums">{cw.quality.ppm.toLocaleString()}</span>}
              foot={`${cw.quality.reject_qty.toLocaleString()} rej`}
              onClick={() => openWeek('ppm')}
            />
            <KPICell
              label="NCRs"
              value={<span className="text-2xl font-semibold tabular-nums">{cw.quality.ncrs.toLocaleString()}</span>}
              onClick={() => openWeek('ncrs')}
            />
          </div>
        </SectionFrame>

        <SectionFrame title="Delivery" accent="green" subtitle={!isLive ? 'full week' : (cw.frozen ? 'Frozen at end of day 5' : 'Mon → today')}>
          <div className={`grid gap-3 items-start ${showOTIF && showOTD ? 'grid-cols-3' : 'grid-cols-2'}`}>
            {showOTIF && (
              <KPICell
                label="OTIF"
                value={<CircularProgress percentage={cw.delivery.otif_pct} size={80} strokeWidth={6} color={performanceColor(cw.delivery.otif_pct, otifTarget)} decimals={kpiDecimals} />}
                foot={`${cw.delivery.otif_met}/${cw.delivery.total_due} PO lines`}
                onClick={() => openWeek('otif_due')}
              />
            )}
            {showOTD && (
              <KPICell
                label="OTD"
                value={<CircularProgress percentage={cw.delivery.otd_pct} size={80} strokeWidth={6} color={performanceColor(cw.delivery.otd_pct, otdTarget)} decimals={kpiDecimals} />}
                foot={`${cw.delivery.otd_met}/${cw.delivery.otd_due ?? cw.delivery.total_due} receipts on time`}
                // Customer review 11 Sep (item 31): open on the denominator
                // the caption divides by (All / On time / Not on time).
                onClick={() => openWeek('otd_due')}
              />
            )}
            <KPICell
              label="Appeals"
              value={<span className="text-2xl font-semibold tabular-nums">{cw.delivery.appeals}</span>}
              onClick={() => openWeek('appeals')}
            />
          </div>
        </SectionFrame>

        <SectionFrame title="Cost" accent="blue" subtitle={`PO lines due ${periodWord} week`}>
          <div className="grid grid-cols-3 gap-3">
            <KPICell
              label="Open PO Lines"
              value={<span className="text-2xl font-semibold tabular-nums">{cw.cost.open_lines.toLocaleString()}</span>}
              foot={`of ${cw.cost.total_lines.toLocaleString()} lines · ${formatCurrency(cw.cost.open_value)}`}
              onClick={() => openWeek('open')}
            />
            <KPICell
              label="Received"
              value={<span className="text-sm font-semibold leading-tight tabular-nums">{formatCurrency(cw.cost.received_value)}</span>}
              foot={`of ${formatCurrency(cw.cost.total_value)} committed`}
              onClick={() => openWeek('received')}
            />
            <KPICell
              label="Arrears"
              value={<span className="text-2xl font-semibold tabular-nums">{cw.cost.arrears_lines.toLocaleString()}</span>}
              foot={`past-due lines · ${formatCurrency(cw.cost.arrears_value)}`}
              onClick={() => setModal({ mode: 'arrears', periodEnd: data.today, periodLabel: 'All past-due' })}
            />
          </div>
        </SectionFrame>
      </div>

      <div className={`grid gap-4 ${showOTIF && showOTD ? 'grid-cols-3' : (showOTIF || showOTD) ? 'grid-cols-2' : 'grid-cols-1'}`}>
        <Card className="border-slate-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-slate-700">Deliveries by Day</CardTitle>
            <p className="text-[11px] text-muted-foreground">Monday through Sunday • {periodWord} week — click a segment to drill in</p>
          </CardHeader>
          <CardContent>
            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={deliveriesByDay} margin={{ top: 5, right: 10, bottom: 0, left: -10 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                  <XAxis dataKey="day" tick={{ fontSize: 11, fill: '#64748b' }} axisLine={{ stroke: '#cbd5e1' }} tickLine={false} />
                  <YAxis tick={{ fontSize: 11, fill: '#64748b' }} axisLine={false} tickLine={false} />
                  <Tooltip cursor={{ fill: '#e2e8f0', fillOpacity: 0.7 }} contentStyle={{ fontSize: 12, borderRadius: 6 }} />
                  <Legend wrapperStyle={{ fontSize: 11 }} iconType="circle" iconSize={8} />
                  <Bar dataKey="onTime"  stackId="a" fill={PALETTE.onTime} name="On time" cursor="pointer" activeBar={{ stroke: '#0f172a', strokeWidth: 1.5 }} onClick={onSegmentClick('onTime')} />
                  <Bar dataKey="early"   stackId="a" fill={PALETTE.early}  name="Early"   cursor="pointer" activeBar={{ stroke: '#0f172a', strokeWidth: 1.5 }} onClick={onSegmentClick('early')} />
                  <Bar dataKey="late"    stackId="a" fill={PALETTE.late}   name="Late"    cursor="pointer" activeBar={{ stroke: '#0f172a', strokeWidth: 1.5 }} onClick={onSegmentClick('late')} />
                  <Bar dataKey="pending" stackId="a" fill={PALETTE.pending} name="Pending" radius={[3, 3, 0, 0]} cursor="pointer" activeBar={{ stroke: '#0f172a', strokeWidth: 1.5 }} onClick={onSegmentClick('pending')} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {showOTD && (
          <Card className="border-slate-200">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-slate-700">OTD Mix</CardTitle>
              <p className="text-[11px] text-muted-foreground">{isLive ? 'Week to date' : 'Full week'} • {(cw.delivery.total_otd ?? cw.delivery.total_with_status).toLocaleString()} receipts{(cw.delivery.total_otd ?? 0) > cw.delivery.total_with_status ? ` (${((cw.delivery.total_otd ?? 0) - cw.delivery.total_with_status).toLocaleString()} not yet judged)` : ''} — click a slice to drill in</p>
            </CardHeader>
            <CardContent>
              <div className="h-56">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={otdData} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={56} outerRadius={84}
                         paddingAngle={otdEmpty ? 0 : 2} stroke="none" onClick={handleOtdSlice}
                         activeShape={{ stroke: '#0f172a', strokeWidth: 1.5 } as unknown as Record<string, unknown>}>
                      {otdData.map((entry, i) => (
                        <Cell key={i} fill={entry.color} cursor={entry.mode ? 'pointer' : 'default'} />
                      ))}
                    </Pie>
                    <Tooltip contentStyle={{ fontSize: 12, borderRadius: 6 }} />
                    <Legend wrapperStyle={{ fontSize: 11 }} iconType="circle" iconSize={8} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        )}

        {showOTIF && (
          <Card className="border-slate-200">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-slate-700">OTIF Mix</CardTitle>
              <p className="text-[11px] text-muted-foreground">{isLive ? 'This week' : 'That week'} • {(otifPass + otifPending + otifArrears + otifFail).toLocaleString()} PO lines — click a slice to drill in</p>
            </CardHeader>
            <CardContent>
              <div className="h-56">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={otifData} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={56} outerRadius={84}
                         paddingAngle={otifEmpty ? 0 : 2} stroke="none" onClick={handleOtifSlice}
                         activeShape={{ stroke: '#0f172a', strokeWidth: 1.5 } as unknown as Record<string, unknown>}>
                      {otifData.map((entry, i) => (
                        <Cell key={i} fill={entry.color} cursor={entry.mode ? 'pointer' : 'default'} />
                      ))}
                    </Pie>
                    <Tooltip contentStyle={{ fontSize: 12, borderRadius: 6 }} />
                    <Legend wrapperStyle={{ fontSize: 11 }} iconType="circle" iconSize={8} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {modal && (
        <POLineDrilldownModal
          open
          onClose={() => setModal(null)}
          mode={modal.mode}
          filters={filters}
          periodStart={modal.periodStart}
          periodEnd={modal.periodEnd}
          periodGrain={modal.periodGrain}
          periodLabel={modal.periodLabel}
          currency={data.currency}
          tabCounts={modal.tabCounts}
          tabContext={modal.tabContext}
          onDataChanged={onRefresh}
        />
      )}
    </div>
  );
}
